import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  MdRestartAlt,
  MdLogin,
  MdLogout,
  MdHomeWork,
  MdMeetingRoom,
  MdApartment,
  MdFavoriteBorder,
  MdSlideshow,
  MdBolt,
} from 'react-icons/md';
import dayjs from 'dayjs';
import Pagination from 'pages/Pagination';
import { apiGet } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';
import useToastContext from 'hooks/useToastContext';
import ActivityNav from './ActivityNav';
import { ACTIVITY_LABELS, BRAND, BRAND_HOVER, BRAND_SOFT } from './constants';
import {
  agentDisplayName,
  avatarColorFromId,
  formatExact,
  formatLiveStamp,
  formatRelative,
  getInitials,
  parsePage,
} from './utils';

const PAGE_SIZE = 10;

const datePickerSlotProps = {
  sx: {
    width: 160,
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': { borderColor: BRAND },
      '&.Mui-focused fieldset': { borderColor: BRAND },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
  },
};

function activityIcon(type) {
  if (type === 'login') return MdLogin;
  if (type === 'logout') return MdLogout;
  if (type?.includes('property') && type?.includes('view')) return MdHomeWork;
  if (type?.includes('unit') && type?.includes('view')) return MdMeetingRoom;
  if (type?.includes('project') && type?.includes('view')) return MdApartment;
  if (type?.includes('presentation')) return MdSlideshow;
  if (
    type?.includes('wishlist') ||
    type?.includes('follow') ||
    type?.includes('unfollow')
  ) {
    return MdFavoriteBorder;
  }
  return MdBolt;
}

function Feed() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const notification = useToastContext();

  const page = parsePage(searchParams.get('page'), 1);
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const agentId = searchParams.get('agentId') || '';
  const activityType = searchParams.get('activityType') || '';
  const entityType = searchParams.get('entityType') || '';

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    agents: [],
    activityTypes: [],
    entityTypes: [],
  });

  const updateParams = (patch, { resetPage = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (resetPage) next.set('page', '1');
    setSearchParams(next);
  };

  const handleReset = () => {
    setSearchParams({});
  };

  const handlePageClick = (event) => {
    updateParams({ page: event.selected + 1 });
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await apiGet(apiPath.activityFilterOptions);
      if (res?.data?.success) {
        setFilterOptions(res.data.results || {});
      }
    } catch (error) {
      // non-blocking
    }
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
      const payload = {
        page,
        pageSize: PAGE_SIZE,
      };
      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;
      if (agentId) payload.agentId = agentId;
      if (activityType) payload.activityType = activityType;
      if (entityType) payload.entityType = entityType;

      const res = await apiGet(apiPath.activityFeed, payload);
      if (res?.data?.success) {
        const results = res.data.results || {};
        setRows(results.docs || []);
        setTotalPages(results.totalPages || 0);
        setTotalItems(results.totalDocs || 0);
        setUpdatedAt(new Date().toISOString());
      } else {
        notification.error(res?.data?.message || 'Unable to load activity feed.');
      }
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to load activity feed.'
      );
    }
  }, [
    activityType,
    agentId,
    endDate,
    entityType,
    notification,
    page,
    startDate,
  ]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  return (
    <div className="px-4 lg:px-7 py-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            Agent Activity Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-1 mb-0">
            Monitor engagement and performance across all agents.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{
              background: BRAND,
              boxShadow: `0 0 0 3px ${BRAND_SOFT}`,
            }}
          />
          Live · {formatLiveStamp(updatedAt)}
        </div>
      </div>

      <ActivityNav />

      <Card
        variant="outlined"
        sx={{ mb: 2.5, borderRadius: 2, borderColor: '#E5E7EB' }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
            py: '16px !important',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="From"
              inputFormat="DD-MM-YYYY"
              value={startDate ? dayjs(startDate) : null}
              maxDate={endDate ? dayjs(endDate) : undefined}
              onChange={(value) =>
                updateParams(
                  {
                    startDate:
                      value && dayjs(value).isValid()
                        ? dayjs(value).format('YYYY-MM-DD')
                        : '',
                  },
                  { resetPage: true }
                )
              }
              renderInput={(params) => (
                <TextField {...params} size="small" sx={datePickerSlotProps.sx} />
              )}
            />
            <DesktopDatePicker
              label="To"
              inputFormat="DD-MM-YYYY"
              value={endDate ? dayjs(endDate) : null}
              minDate={startDate ? dayjs(startDate) : undefined}
              onChange={(value) =>
                updateParams(
                  {
                    endDate:
                      value && dayjs(value).isValid()
                        ? dayjs(value).format('YYYY-MM-DD')
                        : '',
                  },
                  { resetPage: true }
                )
              }
              renderInput={(params) => (
                <TextField {...params} size="small" sx={datePickerSlotProps.sx} />
              )}
            />
          </LocalizationProvider>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              displayEmpty
              value={agentId}
              onChange={(e) =>
                updateParams({ agentId: e.target.value }, { resetPage: true })
              }
            >
              <MenuItem value="">All agents</MenuItem>
              {(filterOptions.agents || []).map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={activityType}
              onChange={(e) =>
                updateParams(
                  { activityType: e.target.value },
                  { resetPage: true }
                )
              }
            >
              <MenuItem value="">All activity</MenuItem>
              {(filterOptions.activityTypes || []).map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={entityType}
              onChange={(e) =>
                updateParams(
                  { entityType: e.target.value },
                  { resetPage: true }
                )
              }
            >
              <MenuItem value="">All entities</MenuItem>
              {(filterOptions.entityTypes || []).map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            startIcon={<MdRestartAlt />}
            onClick={handleReset}
            sx={{
              color: '#6B7280',
              textTransform: 'none',
              ml: 'auto',
              '&:hover': { color: BRAND_HOVER, background: BRAND_SOFT },
            }}
          >
            Reset
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}>
        <CardContent sx={{ px: 0, pb: '8px !important' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600, pl: 3 }}>
                  AGENT
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                  ACTIVITY
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                  REFERENCE
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600, pr: 3 }}>
                  WHEN
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const Icon = activityIcon(row.activityType);
                return (
                  <TableRow
                    key={row._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (row.agent?._id) {
                        navigate(`/activity-tracker/agents/${row.agent._id}`);
                      }
                    }}
                  >
                    <TableCell sx={{ pl: 3 }}>
                      <div className="flex items-center gap-2">
                        <Avatar
                          src={row.agent?.profilePic || undefined}
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: 13,
                            bgcolor: avatarColorFromId(row.agent?._id),
                          }}
                        >
                          {getInitials(
                            row.agent?.firstName,
                            row.agent?.lastName
                          )}
                        </Avatar>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                          {agentDisplayName(row.agent)}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                          style={{ background: BRAND_SOFT, color: BRAND }}
                        >
                          <Icon size={16} />
                        </span>
                        <Typography sx={{ fontSize: 14 }}>
                          {row.label ||
                            ACTIVITY_LABELS[row.activityType] ||
                            row.activityType}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {row.reference?.label || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                        {row.entitySub || 'Session'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pr: 3 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {formatRelative(row.createdAt)}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                        {formatExact(row.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 5, color: '#9CA3AF' }}
                  >
                    No activities found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalItems > 0 && (
            <div className="px-3">
              <p className="text-sm text-gray-500 mb-0 pt-3">
                Showing {(page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, totalItems)} of {totalItems}{' '}
                activities
              </p>
              <Pagination
                page={page}
                handlePageClick={handlePageClick}
                options={{
                  pageCount: totalPages,
                  totalItems,
                  pageRangeDisplayed: 5,
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Feed;
